import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { GlassPanel } from '../components/ui/GlassPanel';
import { Button } from '../components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
import {
  Palette,
  Zap,
  Upload,
  Users,
  BookOpen,
  Check,
  ChevronRight,
  ChevronLeft,
} from 'lucide-react';

interface OnboardingStep {
  key: string;
  title: string;
  description: string;
  icon: any;
  completed: boolean;
  completedAt?: string;
}

export function OnboardingPage() {
  const { currentTenant } = useAuth();
  const navigate = useNavigate();
  const [progress, setProgress] = useState<any>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);

  const steps: OnboardingStep[] = [
    {
      key: 'branding',
      title: 'Customize Your Brand',
      description: 'Set up your logo, colors, and domain to match your brand identity',
      icon: Palette,
      completed: progress?.step_branding_completed || false,
      completedAt: progress?.step_branding_completed_at,
    },
    {
      key: 'integration',
      title: 'Connect Your Tools',
      description: 'Link your POS, accounting, and other systems for automatic data sync',
      icon: Zap,
      completed: progress?.step_integration_completed || false,
      completedAt: progress?.step_integration_completed_at,
    },
    {
      key: 'import',
      title: 'Import Your Data',
      description: 'Upload historical sales data or sync from your integrated systems',
      icon: Upload,
      completed: progress?.step_data_import_completed || false,
      completedAt: progress?.step_data_import_completed_at,
    },
    {
      key: 'team',
      title: 'Invite Your Team',
      description: 'Add team members and assign roles for collaboration',
      icon: Users,
      completed: progress?.step_team_invited || false,
      completedAt: progress?.step_team_invited_at,
    },
    {
      key: 'recipe',
      title: 'Create Your First Recipe',
      description: 'Build a recipe to start tracking consumption and costs',
      icon: BookOpen,
      completed: progress?.step_first_recipe_completed || false,
      completedAt: progress?.step_first_recipe_completed_at,
    },
  ];

  useEffect(() => {
    if (currentTenant) {
      loadProgress();
    }
  }, [currentTenant]);

  const loadProgress = async () => {
    try {
      const { data, error } = await supabase
        .from('onboarding_progress')
        .select('*')
        .eq('tenant_id', currentTenant!)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setProgress(data);
        const firstIncomplete = steps.findIndex(
          (step) => !data[`step_${step.key}_completed`]
        );
        setCurrentStep(firstIncomplete >= 0 ? firstIncomplete : steps.length - 1);
      } else {
        await supabase.from('onboarding_progress').insert({
          tenant_id: currentTenant,
        });
        await loadProgress();
      }
    } catch (error) {
      console.error('Error loading progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const markStepComplete = async (stepKey: string) => {
    try {
      await supabase
        .from('onboarding_progress')
        .update({
          [`step_${stepKey}_completed`]: true,
          [`step_${stepKey}_completed_at`]: new Date().toISOString(),
        })
        .eq('tenant_id', currentTenant!);

      await loadProgress();
    } catch (error) {
      console.error('Error updating progress:', error);
    }
  };

  const skipStep = async (stepKey: string) => {
    try {
      const skipped = progress?.skipped_steps || [];
      await supabase
        .from('onboarding_progress')
        .update({
          skipped_steps: [...skipped, stepKey],
        })
        .eq('tenant_id', currentTenant!);

      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      }
    } catch (error) {
      console.error('Error skipping step:', error);
    }
  };

  const completeOnboarding = async () => {
    try {
      await supabase
        .from('onboarding_progress')
        .update({
          is_completed: true,
          completed_at: new Date().toISOString(),
        })
        .eq('tenant_id', currentTenant!);

      navigate('/dashboard');
    } catch (error) {
      console.error('Error completing onboarding:', error);
    }
  };

  const completedCount = steps.filter((s) => s.completed).length;
  const progressPercent = (completedCount / steps.length) * 100;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-400" />
        </div>
      </DashboardLayout>
    );
  }

  const current = steps[currentStep];
  const Icon = current.icon;

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Welcome to Vibe App</h1>
          <p className="text-white/60">
            Let's get you set up in just a few steps. This will only take a few minutes.
          </p>
        </div>

        <GlassPanel className="p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-white/80">
              Step {currentStep + 1} of {steps.length}
            </span>
            <span className="text-sm font-medium text-white/80">
              {completedCount} of {steps.length} completed
            </span>
          </div>
          <div className="relative h-2 bg-white/10 rounded-full overflow-hidden">
            <div
              className="absolute left-0 top-0 h-full bg-gradient-to-r from-primary-500 to-primary-600 transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </GlassPanel>

        <div className="grid grid-cols-5 gap-4">
          {steps.map((step, index) => {
            const StepIcon = step.icon;
            return (
              <button
                key={step.key}
                onClick={() => setCurrentStep(index)}
                className={`p-4 rounded-xl border-2 transition-all ${
                  currentStep === index
                    ? 'border-primary-400 bg-primary-500/10'
                    : step.completed
                    ? 'border-success-400/30 bg-success-500/5'
                    : 'border-white/10 bg-white/5 hover:border-white/20'
                }`}
              >
                <div className="flex flex-col items-center gap-2">
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      step.completed
                        ? 'bg-success-500/20 border border-success-400/30'
                        : currentStep === index
                        ? 'bg-primary-500/20 border border-primary-400/30'
                        : 'bg-white/5 border border-white/10'
                    }`}
                  >
                    {step.completed ? (
                      <Check className="w-6 h-6 text-success-400" />
                    ) : (
                      <StepIcon
                        className={`w-6 h-6 ${
                          currentStep === index ? 'text-primary-400' : 'text-white/40'
                        }`}
                      />
                    )}
                  </div>
                  <span className="text-xs text-center text-white/60">{step.title}</span>
                </div>
              </button>
            );
          })}
        </div>

        <GlassPanel className="p-8">
          <div className="flex items-start gap-6 mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center flex-shrink-0">
              <Icon className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-white mb-2">{current.title}</h2>
              <p className="text-white/70">{current.description}</p>
            </div>
          </div>

          {current.key === 'branding' && (
            <div className="space-y-4">
              <p className="text-white/60">
                Your brand identity makes this platform truly yours. Customize colors, upload your
                logo, and configure your domain.
              </p>
              <div className="flex gap-3">
                <Button onClick={() => navigate('/branding')}>Go to Branding Settings</Button>
                <Button variant="secondary" onClick={() => markStepComplete('branding')}>
                  Mark as Complete
                </Button>
              </div>
            </div>
          )}

          {current.key === 'integration' && (
            <div className="space-y-4">
              <p className="text-white/60">
                Connect your POS, accounting, and ERP systems to automate data synchronization and
                eliminate manual entry.
              </p>
              <div className="flex gap-3">
                <Button onClick={() => navigate('/integrations')}>
                  Browse Integration Marketplace
                </Button>
                <Button variant="secondary" onClick={() => markStepComplete('integration')}>
                  Mark as Complete
                </Button>
              </div>
            </div>
          )}

          {current.key === 'import' && (
            <div className="space-y-4">
              <p className="text-white/60">
                Import your historical sales data from CSV/Excel files or sync automatically from
                connected integrations.
              </p>
              <div className="flex gap-3">
                <Button onClick={() => navigate('/imports')}>Import Sales Data</Button>
                <Button variant="secondary" onClick={() => markStepComplete('import')}>
                  Mark as Complete
                </Button>
              </div>
            </div>
          )}

          {current.key === 'team' && (
            <div className="space-y-4">
              <p className="text-white/60">
                Invite team members to collaborate. Assign roles like Manager or Staff to control
                access levels.
              </p>
              <div className="flex gap-3">
                <Button onClick={() => navigate('/settings')}>Manage Team</Button>
                <Button variant="secondary" onClick={() => markStepComplete('team')}>
                  Mark as Complete
                </Button>
              </div>
            </div>
          )}

          {current.key === 'recipe' && (
            <div className="space-y-4">
              <p className="text-white/60">
                Create recipes to track consumption, calculate costs, and analyze nutrition for your
                menu items.
              </p>
              <div className="flex gap-3">
                <Button onClick={() => navigate('/items')}>Create First Recipe</Button>
                <Button variant="secondary" onClick={() => markStepComplete('recipe')}>
                  Mark as Complete
                </Button>
              </div>
            </div>
          )}
        </GlassPanel>

        <div className="flex items-center justify-between">
          <Button
            variant="secondary"
            onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
            disabled={currentStep === 0}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>

          <Button variant="secondary" onClick={() => skipStep(current.key)}>
            Skip This Step
          </Button>

          {currentStep < steps.length - 1 ? (
            <Button onClick={() => setCurrentStep(currentStep + 1)}>
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={completeOnboarding}>
              Complete Onboarding
              <Check className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
